import useStory, { span } from './useStory';
import { Explainer } from './GuideParts';

// GUIDE 6'S ANIMATION (20 Oct 2026): an AC goes up on a wall, the copper
// pipe runs out metre by metre, and the bill beside it adds each line with
// its price. Prices come from the rate card (bill() in data/guideInstall.js),
// with GST. Plays once in view, rests fully drawn, Replay.
//
// Props: lines (bill().lines), total, copperM, watch, sources.

const inr = (n) => `₹${Math.round(n).toLocaleString('en-IN')}`;
const WHEN = { fit: [0, 0.18], test: [0.22, 0.36], copper: [0.42, 0.66], drain: [0.7, 0.76], wire: [0.76, 0.82], stand: [0.82, 0.9], remove: [0.9, 0.95] };

export default function InstallBill({ lines, copperM, watch, sources }) {
  const story = useStory(20000);
  const t = story.t;
  const k = (key) => span(t, WHEN[key][0], WHEN[key][1]);
  const shown = lines.map((l) => {
    const f = k(l.key);
    return { ...l, f, amount: l.key === 'copper' ? l.amount * f : l.amount };
  }).filter((l) => l.f > 0);
  const total = shown.reduce((a, l) => a + l.amount, 0);
  const pipe = k('copper');
  const metres = Math.round(pipe * copperM);
  const fit = k('fit');
  const tested = k('test') >= 1;
  const stand = lines.some((l) => l.key === 'stand') ? k('stand') : 0;

  return (
    <Explainer title={watch.title} captions={watch.captions} story={story} note={watch.note} noteSrc={watch.noteSrc} sources={sources}>
      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <svg viewBox="0 0 300 180" width="100%" role="img" aria-label="An AC being fitted: indoor unit on the wall, pipe through the wall to the outdoor unit" className="block h-auto rounded-xl border border-gray-200 bg-gray-50">
          <text x="12" y="20" fontSize="12" fill="#6b7280">Inside</text>
          <text x="288" y="20" fontSize="12" fill="#6b7280" textAnchor="end">Outside</text>
          <rect x="140" y="0" width="22" height="180" fill="#e5e7eb" />
          <g opacity={fit} transform={`translate(0 ${(1 - fit) * -12})`}>
            <rect x="22" y="40" width="96" height="30" rx="8" fill="#fff" stroke="#9ca3af" />
            <rect x="32" y="62" width="76" height="3" rx="1.5" fill="#cbd5e1" />
          </g>
          <g opacity={fit}>
            <rect x="190" y="98" width="84" height="56" rx="7" fill="#fff" stroke="#9ca3af" />
            <circle cx="222" cy="126" r="18" fill="#f9fafb" stroke="#d1d5db" />
          </g>
          {stand > 0 && (
            <g opacity={stand} stroke="#4b5563" strokeWidth="3" strokeLinecap="round">
              <path d="M162 158h112M200 158v12M264 158v12" />
            </g>
          )}
          <path d="M118 56H151V126H190" fill="none" stroke="#e5e7eb" strokeWidth="6" strokeLinejoin="round" />
          <path d="M118 56H151V126H190" fill="none" stroke="#b45309" strokeWidth="6" strokeLinejoin="round"
            pathLength="1" strokeDasharray="1" strokeDashoffset={1 - pipe} />
          {k('drain') > 0 && <path d="M40 70V110H150" fill="none" stroke="#6b7280" strokeWidth="3" strokeDasharray="5 4" opacity={k('drain')} />}
          {pipe > 0 && <text x="170" y="86" fontSize="13" fontWeight="800" fill="#92400e">{metres} m</text>}
          {tested && <text x="151" y="42" fontSize="12" fontWeight="700" fill="#15803d" textAnchor="middle">Tested</text>}
        </svg>
        <div className="grid content-start gap-1.5 rounded-xl border border-gray-200 bg-white p-3">
          <p className="text-[13px] font-bold uppercase tracking-wide text-gray-500">Your bill</p>
          {shown.map((l) => (
            <p key={l.key} className="flex items-baseline justify-between gap-2 text-[15px] text-gray-700">
              <span>{l.key === 'copper' ? `Copper pipe, ${metres} m` : l.label}</span>
              <span className="font-bold tabular-nums text-gray-900">{inr(l.amount)}</span>
            </p>
          ))}
          <p className="mt-1 flex items-baseline justify-between gap-2 border-t border-gray-200 pt-2 text-[16px] font-extrabold text-gray-900">
            <span>Total, with GST</span><span className="text-[20px] tabular-nums">{inr(total)}</span>
          </p>
        </div>
      </div>
    </Explainer>
  );
}
